import { useCallback, useEffect, useState } from 'react';

/**
 * Tracks whether the user has "submitted" their picks for a given event.
 *
 * Picks already auto-save on each click; "submitted" is a UX flourish so the
 * bracket feels concluded. Stored in localStorage per user+event — no API,
 * no cross-device sync.
 *
 * `userId` is part of the key so multiple users sharing a device don't see
 * each other's submitted state. When userId is undefined (signed out) the
 * hook is a no-op — caller should hide the submit button in that case.
 */
export function usePickemSubmitted(
  eventId: string,
  userId: string | undefined,
): {
  isSubmitted: boolean;
  submittedAt: number | null;
  markSubmitted: () => void;
  clearSubmitted: () => void;
} {
  const key =
    userId && eventId ? `pickem-submitted-${userId}-${eventId}` : null;

  const [submittedAt, setSubmittedAt] = useState<number | null>(() => {
    if (typeof window === 'undefined' || !key) return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  });

  // Re-read when the key changes (login switch / event change).
  useEffect(() => {
    if (typeof window === 'undefined' || !key) {
      setSubmittedAt(null);
      return;
    }
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? Number(raw) : NaN;
    setSubmittedAt(Number.isFinite(parsed) ? parsed : null);
  }, [key]);

  const markSubmitted = useCallback(() => {
    if (typeof window === 'undefined' || !key) return;
    const now = Date.now();
    window.localStorage.setItem(key, String(now));
    setSubmittedAt(now);
  }, [key]);

  const clearSubmitted = useCallback(() => {
    if (typeof window === 'undefined' || !key) return;
    window.localStorage.removeItem(key);
    setSubmittedAt(null);
  }, [key]);

  return {
    isSubmitted: submittedAt !== null,
    submittedAt,
    markSubmitted,
    clearSubmitted,
  };
}
